import React, { useState } from "react";
import { AppHeader } from "./CommunitySelection";
import Dashboard from "./Dashboard";
import { REFLECTION_OPTIONS, REFLECTION_REMINDER, currency } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { ArrowRight, Check } from "lucide-react";

export const EventScreen = ({ event, state, communityName, onSubmit, submitting, quick = false }) => {
  const { t } = useLang();
  const [choiceId, setChoiceId] = useState(null);
  const [reflection, setReflection] = useState(null);

  const submit = () => {
    if (!choiceId || submitting) return;
    onSubmit(choiceId, event.reflect ? reflection : null);
  };

  return (
    <div className="min-h-screen pb-28">
      <AppHeader subtitle={communityName} />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-vin/70">
            {quick ? t("Decision") : t("Month")} {event.month} {t("of")} {event.total_months}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: event.total_months }).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-5 rounded-full ${i < event.month - 1 ? "bg-vin" : i === event.month - 1 ? "bg-vin/60" : "bg-slate-200"}`}
              />
            ))}
          </div>
        </div>

        <Dashboard state={state} quick={quick} />

        <div key={event.id} className="mt-6 animate-rise">
          <div className="bg-white rounded-lg border border-[var(--line)] shadow-sm p-6 sm:p-8">
            <h2 className="font-display text-2xl font-semibold text-ink leading-snug">{event.title}</h2>
            <p className="mt-3 text-base text-ink-soft leading-relaxed">{event.narrative}</p>

            {event.voice && (
              <blockquote className="mt-5 border-l-2 border-vin/40 pl-4 py-1">
                <p className="text-[15px] italic text-vin-dark leading-relaxed">"{event.voice}"</p>
                <footer className="mt-1 text-xs uppercase tracking-[0.14em] text-ink-soft">{t("In their words")}</footer>
              </blockquote>
            )}

            <p className="mt-7 text-sm font-semibold text-vin-dark uppercase tracking-[0.12em]">
              {t("What do you do?")}
            </p>
            <div className="mt-3 space-y-3">
              {event.choices.map((c) => {
                const active = choiceId === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`choice-${c.id}`}
                    onClick={() => setChoiceId(c.id)}
                    className={`w-full text-left rounded-lg border p-4 sm:p-5 transition-colors duration-200 ${
                      active ? "border-vin bg-vin/5 ring-1 ring-vin" : "border-[var(--line)] hover:border-vin/50 bg-white"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          active ? "border-vin bg-vin" : "border-slate-300"
                        }`}
                      >
                        {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-semibold text-ink text-[15px]">{c.label}</div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {c.cost > 0 && (
                              <span className="text-xs font-semibold text-[#8E2929] bg-[#8E2929]/8 px-2.5 py-1 rounded-full">
                                {t("costs ~")}{currency(c.cost)}
                              </span>
                            )}
                            {c.monthly > 0 && (
                              <span className="text-xs font-semibold text-vin-purple bg-vin-purple/8 px-2.5 py-1 rounded-full whitespace-nowrap">
                                {t("then ~")}{currency(c.monthly)}{t("/mo")}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-ink-soft mt-0.5 leading-relaxed">{c.description}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {event.reflect && choiceId && (
              <div className="mt-7 pt-6 border-t border-[var(--line)] animate-rise">
                <p className="text-sm text-vin-dark italic leading-relaxed">{t(REFLECTION_REMINDER)}</p>
                {event.prompt && (
                  <p className="mt-3 font-display text-lg font-semibold text-ink">{event.prompt}</p>
                )}
                <p className="mt-2 text-xs text-ink-soft">{t("Sit with it — or note a response. Optional.")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {REFLECTION_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      data-testid={`reflection-${opt.slice(0, 10)}`}
                      onClick={() => setReflection(reflection === opt ? null : opt)}
                      className={`text-sm px-4 py-2 rounded-full border transition-colors duration-200 ${
                        reflection === opt ? "border-vin bg-vin text-white" : "border-[var(--line)] text-ink-soft hover:border-vin/50"
                      }`}
                    >
                      {t(opt)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white/95 border-t border-[var(--line)] backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-4 flex justify-end">
          <button
            data-testid="continue-decision-button"
            onClick={submit}
            disabled={!choiceId || submitting}
            className="inline-flex items-center gap-2 bg-vin text-white font-semibold px-7 py-3.5 rounded-full hover:bg-vin-hover transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "…" : t("Continue")}
            <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventScreen;
